import { useEffect, useRef } from "react"
import PubSub from "pubsub-js"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import { clearUserLocalStorage } from "aldoo-ra/PlatformAuth/actions/logout"
import Value from "aldoo-ra/Value"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import TextBundle from "aldoo-ra/CMS/text-bundle"

/**
 * ResetStorage — recovery hatch for users stuck with corrupt/stale browser data.
 *
 * Send a stuck user to  https://transfer.aldoo.com/?reset=1
 * On load this wipes the persisted auth/profile (localStorage) plus the in-memory
 * copies, strips the ?reset flag so a refresh won't re-run, then shows a friendly
 * dialog with a single "Sign in again" button. No DevTools / clear-site-data needed.
 */
export default function ResetStorage() {
  const [, setAuth] = PlatformAuth.useAuth()
  const [, setProfile] = Value("user-profile")
  const done = useRef(false)

  const texts = TextBundle({ id: "transfer_reset_storage" })

  useEffect(() => {
    if (done.current) return
    if (typeof window === "undefined") return

    const params = new URLSearchParams(window.location.search)
    if (params.get("reset") !== "1") return
    done.current = true

    // 1. wipe persisted + in-memory auth/profile
    clearUserLocalStorage()
    // also drop the device id so a fresh one is minted on next signin (ensure-device self-heals)
    localStorage.removeItem("deviceID")
    // purge any stale OAuth handshake artifacts so they can't auto-fire/loop signin
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith("oauth-result-"))
        .forEach((k) => localStorage.removeItem(k))
      Object.keys(sessionStorage)
        .filter((k) => k.startsWith("oauth-inflight-"))
        .forEach((k) => sessionStorage.removeItem(k))
    } catch {}
    setAuth(null)
    setProfile(null)

    // 2. strip ?reset=1 so a refresh won't re-trigger the wipe
    params.delete("reset")
    const query = params.toString()
    const clean = window.location.pathname + (query ? "?" + query : "")
    window.history.replaceState({}, "", clean)

    // 3. tell the user + offer to sign in again (fires on button tap or dialog close)
    const openSignin = () =>
      PubSub.publish("signin", { redirect: "/account/my-transfers", forceDialog: true })

    // defer one tick so PromptDialogProvider (mounted in AldooBoot) is registered
    setTimeout(() => {
      showPromptDialog({
        title: texts?.reset_title,
        message:
          texts?.reset_message,
        confirmText: texts?.reset_signin_btn,
        showNoBtn: false,
        onConfirm: openSignin,
        onClose: openSignin,
      })
    }, 0)
  }, [texts])

  return null
}
